import React from 'react';

import { LinkOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';

import { useUpsertWebhookConfig } from '../api/upsertWebhookConfig';
import { buildWebhookConfigSchema } from '../schemas/webhookConfigSchema';
import { IWebhookConfig, WEBHOOK_AUTH_TYPE } from '../types';
import { IWebhookFormValues, WebhookForm } from './WebhookForm';
import CancelSubmitFooterModal from '@/components/CancelSubmitFooterModal';
import CustomModal from '@/components/CustomModal';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IEditWebhookModalProps {
  partnerId: string;
  config: IWebhookConfig;
  open: boolean;
  onClose: () => void;
}

export const EditWebhookModal = ({
  partnerId,
  config,
  open,
  onClose,
}: IEditWebhookModalProps) => {
  const addToast = useToasts((state) => state.addItem);

  const { mutateAsync: upsertWebhookConfig, isPending } =
    useUpsertWebhookConfig({ partnerId });

  const formik = useFormik<IWebhookFormValues>({
    initialValues: {
      url: config.url,
      authType: WEBHOOK_AUTH_TYPE.PUBLIC,
    },
    validationSchema: buildWebhookConfigSchema(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const {
          // Ignored for now
          authType: _authType,
          ...apiValues
        } = values;

        await upsertWebhookConfig({
          configId: config.config_id,
          values: { ...config, ...apiValues },
        });
        addToast(
          { msg: 'Webhook URL updated', type: MessageType.Success },
          'root',
          {}
        );
        onClose();
      } catch (err) {
        addToast(
          {
            msg: err instanceof Error ? err.message : 'Update failed',
            type: MessageType.Error,
          },
          'root',
          {}
        );
      }
    },
  });

  const isInvalid = !formik.values.url || !!formik.errors.url;

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <CustomModal
      title={
        <span>
          <LinkOutlined /> Edit Webhook
        </span>
      }
      open={open}
      destroyOnClose
      footer={CancelSubmitFooterModal(
        isInvalid,
        formik.handleSubmit,
        handleCancel,
        isPending
      )}
      onCancel={handleCancel}
      width="50%"
    >
      <WebhookForm formik={formik} />
    </CustomModal>
  );
};
